/**
 * LIS i18n — FR default + AR (RTL). Registered once in the module's onRegister. Keys are namespaced
 * `lis.*`; missing keys fall back to FR then the raw key (never a crash, never a blank). Clinical /
 * status semantics always pair color + glyph + text — the labels here are the text half.
 */
import { registerMessages } from "@/app";

export function registerLisMessages(): void {
  registerMessages("fr", {
    // nav
    "nav.lis": "Automates",
    "nav.lis.dashboard": "Parc analyseurs",
    "nav.lis.onboarding": "Raccorder un automate",
    "nav.lis.monitor": "Flux & quarantaine",
    "nav.lis.chargement": "Chargement positionnel",
    "nav.section.lis": "Laboratoire (LIS)",

    // generic
    "lis.action.search": "Rechercher un modèle, un fabricant…",
    "lis.action.refresh": "Actualiser",
    "lis.action.test": "Tester la connexion",
    "lis.action.back": "Retour",
    "lis.action.next": "Suivant",
    "lis.action.previous": "Précédent",
    "lis.action.cancel": "Annuler",
    "lis.action.confirm": "Confirmer",
    "lis.field.optional": "facultatif",

    // dashboard
    "lis.dashboard.title": "Parc analyseurs",
    "lis.dashboard.subtitle": "État des liaisons automate ↔ laboratoire",
    "lis.dashboard.count": "{n} automate(s) raccordé(s)",
    "lis.dashboard.add": "Raccorder un automate",
    "lis.dashboard.empty.title": "Aucun automate raccordé",
    "lis.dashboard.empty.desc":
      "Raccordez votre premier analyseur en quelques clics depuis le catalogue.",
    "lis.dashboard.col.automate": "Automate",
    "lis.dashboard.col.discipline": "Discipline",
    "lis.dashboard.col.liaison": "Liaison",
    "lis.dashboard.col.sante": "Santé",
    "lis.dashboard.col.actions": "Actions",
    "lis.dashboard.kpi.total": "Automates",
    "lis.dashboard.kpi.actifs": "Liaisons actives",
    "lis.dashboard.kpi.attention": "À surveiller",
    "lis.dashboard.kpi.quarantaine": "En quarantaine",
    "lis.dashboard.detail": "Détail & diagnostic",

    // health states (text half of color+glyph+text)
    "lis.health.actif": "Actif",
    "lis.health.inactif": "Inactif",
    "lis.health.attention": "À surveiller",
    "lis.health.absent": "Config absente",
    "lis.health.unknown": "Inconnu",
    // live link-state (NOVA-021 — real connecte/degrade/deconnecte from the connection plane)
    "lis.health.connecte": "Connecté",
    "lis.health.degrade": "Dégradé",
    "lis.health.deconnecte": "Déconnecté",
    "lis.link.lastSeen": "Vu pour la dernière fois",
    "lis.link.reason": "Motif",

    // connection / config
    "lis.config.protocole": "Protocole",
    "lis.config.transport": "Transport",
    "lis.config.sens": "Sens",
    "lis.config.mode": "Mode d'interaction",
    "lis.config.ip": "Adresse IP",
    "lis.config.port": "Port",
    "lis.config.liaison": "Liaison spécimen",
    "lis.config.actif": "Liaison active",
    "lis.config.save": "Enregistrer la configuration",
    "lis.config.saved": "Configuration enregistrée.",
    "lis.config.saveError": "Échec de l'enregistrement de la configuration.",
    "lis.transport.serie": "Série (RS-232)",
    "lis.transport.tcp": "Réseau (TCP/IP)",
    "lis.sens.unidirectionnel": "Unidirectionnel (push)",
    "lis.sens.bidirectionnel": "Bidirectionnel (host-query)",
    "lis.mode.host_query": "Interrogation hôte",
    "lis.mode.push": "Émission de résultats",
    "lis.mode.download": "Téléchargement",

    // test connection
    "lis.test.running": "Test en cours…",
    "lis.test.ok": "Liaison conforme — l'automate peut dialoguer.",
    "lis.test.fail": "Liaison non conforme.",
    "lis.test.reason.config_absente": "Configuration absente : terminez le raccordement.",
    "lis.test.reason.invalide": "Configuration incompatible avec l'adaptateur.",

    // onboarding wizard
    "lis.onboarding.title": "Raccorder un automate",
    "lis.onboarding.subtitle": "Sélection guidée — du fabricant au branchement",
    "lis.onboarding.step.fabricant": "Fabricant",
    "lis.onboarding.step.modele": "Modèle",
    "lis.onboarding.step.protocole": "Protocole",
    "lis.onboarding.step.connexion": "Branchement",
    "lis.onboarding.step.fabricant.hint": "Choisissez le fabricant de l'analyseur.",
    "lis.onboarding.step.modele.hint": "{n} modèle(s) dans le catalogue Algérie.",
    "lis.onboarding.step.protocole.hint":
      "Le profil pré-remplit le protocole et le transport recommandés — ajustez si besoin.",
    "lis.onboarding.step.connexion.hint":
      "Dernière étape : le code interne et le point de branchement (IP/port ou port série).",
    "lis.onboarding.disciplineFilter": "Discipline",
    "lis.onboarding.disciplineAll": "Toutes les disciplines",
    "lis.onboarding.confidence.verified": "Source vérifiée",
    "lis.onboarding.confidence.inferred": "À confirmer au branchement",
    "lis.onboarding.notes": "Notes de dialecte",
    "lis.onboarding.fabricantNote": "Note fabricant",
    "lis.onboarding.field.code": "Code interne de l'automate",
    "lis.onboarding.field.code.hint": "Identifiant court et unique (ex. BIO-1, HEMATO-A).",
    "lis.onboarding.field.libelle": "Libellé",
    "lis.onboarding.field.ip": "Adresse IP de l'automate",
    "lis.onboarding.field.port": "Port",
    "lis.onboarding.serial.note":
      "Liaison série : le port COM se configure après le raccordement, dans le détail de l'automate.",
    "lis.onboarding.summary": "Récapitulatif",
    "lis.onboarding.submit": "Raccorder l'automate",
    "lis.onboarding.success": "Automate {code} raccordé.",
    "lis.onboarding.error": "Échec du raccordement.",
    "lis.onboarding.noProfiles": "Aucun modèle ne correspond à la recherche.",
    "lis.onboarding.pickFabricant": "Sélectionnez d'abord un fabricant.",
    "lis.onboarding.selected": "Sélectionné",

    // monitor
    "lis.monitor.title": "Flux d'ingestion & quarantaine",
    "lis.monitor.subtitle": "Messages en attente de résolution — rien n'est jamais perdu",
    "lis.monitor.tab.quarantaine": "Quarantaine",
    "lis.monitor.tab.mappings": "Correspondances",
    "lis.monitor.motif.all": "Tous les motifs",
    "lis.monitor.motif.code_inconnu": "Code inconnu",
    "lis.monitor.motif.orphelin": "Spécimen orphelin",
    "lis.monitor.motif.discordance_grave": "Discordance grave",
    "lis.monitor.col.recu": "Reçu",
    "lis.monitor.col.motif": "Motif",
    "lis.monitor.col.code": "Code analyseur",
    "lis.monitor.col.specimen": "Spécimen",
    "lis.monitor.col.valeur": "Valeur brute",
    "lis.monitor.col.action": "Résolution",
    "lis.monitor.empty.title": "File de quarantaine vide",
    "lis.monitor.empty.desc": "Tous les messages reçus ont été rapprochés au catalogue.",
    "lis.monitor.resolve.code": "Créer la correspondance",
    "lis.monitor.mappings.pick": "Choisir un automate",
    "lis.monitor.mappings.empty.title": "Aucune correspondance",
    "lis.monitor.mappings.empty.desc": "Cet automate n'a pas encore de code rapproché.",
    "lis.monitor.mappings.col.code": "Code analyseur",
    "lis.monitor.mappings.col.parametre": "Paramètre labo",
    "lis.monitor.mappings.col.unite": "Unité",
    "lis.monitor.mappings.col.statut": "Statut",
    "lis.monitor.mappings.confirm": "Confirmer la correspondance",
    "lis.monitor.mappings.confirmTitle": "Confirmer la correspondance",
    "lis.monitor.mappings.parametreId": "Identifiant paramètre labo",
    "lis.monitor.mappings.parametreId.hint":
      "Lie le code analyseur à un paramètre du catalogue ; les résultats en quarantaine sont ré-ingérés.",
    "lis.monitor.mappings.confirmed": "Correspondance confirmée — quarantaine ré-ingérée.",
    "lis.monitor.mappings.confirmError": "Échec de la confirmation de la correspondance.",
    "lis.monitor.mappings.statut.propose": "Proposé",
    "lis.monitor.mappings.statut.actif": "Actif",

    // chargement positionnel
    "lis.chargement.title": "Chargement positionnel",
    "lis.chargement.subtitle": "Scellez la portée position → tube avant l'arrivée des résultats",
    "lis.chargement.open": "Nouveau chargement",
    "lis.chargement.openTitle": "Ouvrir un chargement",
    "lis.chargement.automate": "Automate",
    "lis.chargement.reference": "Référence (rack / série)",
    "lis.chargement.created": "Chargement {ref} ouvert.",
    "lis.chargement.createError": "Échec de l'ouverture du chargement.",
    "lis.chargement.lookup": "Ouvrir un chargement existant",
    "lis.chargement.lookup.id": "Identifiant du chargement",
    "lis.chargement.statut.ouvert": "Ouvert",
    "lis.chargement.statut.confirme": "Confirmé (scellé)",
    "lis.chargement.statut.clos": "Clos",
    "lis.chargement.positions": "Positions",
    "lis.chargement.addPosition": "Ajouter une position",
    "lis.chargement.col.position": "Position",
    "lis.chargement.col.tube": "Tube",
    "lis.chargement.field.position": "Position (rack)",
    "lis.chargement.field.tube": "Identifiant du tube",
    "lis.chargement.added": "Position {pos} ajoutée.",
    "lis.chargement.addError": "Échec de l'ajout de la position.",
    "lis.chargement.empty.title": "Aucune position",
    "lis.chargement.empty.desc": "Ajoutez chaque position du rack avant de sceller le chargement.",
    "lis.chargement.confirm": "Sceller le chargement",
    "lis.chargement.confirmTitle": "Sceller le chargement",
    "lis.chargement.confirmWarn":
      "Le scellement est définitif : aucune position ne pourra être ajoutée ensuite. Le nombre de positions devient le nombre de résultats attendus.",
    "lis.chargement.confirmed": "Chargement scellé — la portée positionnelle est active.",
    "lis.chargement.confirmError": "Échec du scellement du chargement.",
    "lis.chargement.close": "Clore le chargement",
    "lis.chargement.closed": "Chargement clos.",
    "lis.chargement.closeError": "Échec de la clôture du chargement.",
    "lis.chargement.sealedNote": "Chargement scellé — modification impossible.",
    "lis.chargement.expected": "{n} résultat(s) attendu(s)",

    // cloud relay agents (NOVA-070)
    "nav.lis.agents": "Agents relais (cloud)",
    "lis.agents.title": "Agents relais",
    "lis.agents.subtitle":
      "Les agents sur site dialoguent avec les automates et relaient leurs résultats vers le cloud.",
    "lis.agents.add": "Enregistrer un agent",
    "lis.agents.addTitle": "Nouvel agent relais",
    "lis.agents.field.nom": "Nom de l'agent (poste / paillasse)",
    "lis.agents.created": "Agent enregistré — code d'appairage généré.",
    "lis.agents.createError": "Échec de l'enregistrement de l'agent.",
    "lis.agents.col.nom": "Agent",
    "lis.agents.col.statut": "État",
    "lis.agents.col.presence": "Dernière présence",
    "lis.agents.col.version": "Version",
    "lis.agents.col.ip": "IP vue",
    "lis.agents.statut.en_attente": "En attente d'appairage",
    "lis.agents.statut.actif": "Actif",
    "lis.agents.statut.revoque": "Révoqué",
    "lis.agents.online": "En ligne",
    "lis.agents.offline": "Hors ligne",
    "lis.agents.empty.title": "Aucun agent relais",
    "lis.agents.empty.desc":
      "Enregistrez un agent pour relayer les résultats d'un automate distant vers ce cloud.",
    "lis.agents.revoke": "Révoquer",
    "lis.agents.revokeTitle": "Révoquer l'agent ?",
    "lis.agents.revokeWarn":
      "Le jeton de l'agent est immédiatement invalidé : il ne pourra plus relayer de résultat. Action réversible par un nouvel appairage.",
    "lis.agents.revoked": "Agent révoqué.",
    "lis.agents.revokeError": "Échec de la révocation.",
    "lis.agents.pairingTitle": "Code d'appairage — affiché une seule fois",
    "lis.agents.pairingNote":
      "Copiez ce code dans l'installateur de l'agent. Il expire bientôt et ne sera plus jamais affiché.",
    "lis.agents.pairingCopy": "Copier le code",
    "lis.agents.pairingCopied": "Code copié.",
    "lis.agents.pairingExpires": "Expire le {at}",
    "lis.agents.done": "Terminé",
    "lis.agents.installTitle": "Installer l'agent relais",
    "lis.agents.installStep1":
      "Installez le paquet Novanalyse Agent sur le poste sur site relié aux automates (voir agent/README.md).",
    "lis.agents.installStep2":
      "Au premier démarrage, l'agent demande le code d'appairage : collez le code ci-dessus.",
    "lis.agents.installStep3":
      "L'agent échange le code contre un jeton (une seule fois), puis dialogue avec les automates en local et relaie les résultats vers ce cloud. La pastille passe « En ligne » au premier battement.",

    // troubleshooting
    "lis.trouble.title": "Diagnostic",
    "lis.trouble.subtitle": "Vérifications rapides en cas de liaison muette",
    "lis.trouble.step1.title": "Tester la liaison",
    "lis.trouble.step1.desc":
      "Lancez le test de connexion : il valide la configuration face aux capacités de l'adaptateur.",
    "lis.trouble.step2.title": "Vérifier le branchement physique",
    "lis.trouble.step2.desc":
      "Liaison réseau : IP et port joignables. Liaison série : câble, port COM, débit et parité conformes au profil.",
    "lis.trouble.step3.title": "Contrôler le sens & le mode",
    "lis.trouble.step3.desc":
      "Un automate en host-query attend une interrogation : vérifiez que le sens est bidirectionnel.",
    "lis.trouble.step4.title": "Surveiller la quarantaine",
    "lis.trouble.step4.desc":
      "Les messages reçus mais non rapprochés s'accumulent en quarantaine, jamais perdus — rapprochez les codes inconnus.",
    "lis.trouble.goMonitor": "Ouvrir la quarantaine",

    // misc
    "lis.na": "—",
  });

  registerMessages("ar", {
    "nav.lis": "الأجهزة",
    "nav.lis.dashboard": "أسطول الأجهزة",
    "nav.lis.onboarding": "ربط جهاز",
    "nav.lis.monitor": "التدفق والحجر",
    "nav.lis.chargement": "التحميل الموضعي",
    "nav.lis.agents": "وكلاء التتابع (سحابة)",
    "nav.section.lis": "المختبر (LIS)",

    "lis.action.search": "ابحث عن طراز أو صانع…",
    "lis.action.refresh": "تحديث",
    "lis.action.test": "اختبار الاتصال",
    "lis.action.back": "رجوع",
    "lis.action.next": "التالي",
    "lis.action.previous": "السابق",
    "lis.action.cancel": "إلغاء",
    "lis.action.confirm": "تأكيد",
    "lis.field.optional": "اختياري",

    "lis.dashboard.title": "أسطول الأجهزة",
    "lis.dashboard.subtitle": "حالة الروابط بين الجهاز والمختبر",
    "lis.dashboard.count": "{n} جهاز مربوط",
    "lis.dashboard.add": "ربط جهاز",
    "lis.dashboard.empty.title": "لا يوجد جهاز مربوط",
    "lis.dashboard.empty.desc": "اربط أول محلل لك بنقرات قليلة من الكتالوج.",
    "lis.dashboard.col.automate": "الجهاز",
    "lis.dashboard.col.discipline": "التخصص",
    "lis.dashboard.col.liaison": "الرابط",
    "lis.dashboard.col.sante": "الحالة",
    "lis.dashboard.col.actions": "إجراءات",
    "lis.dashboard.kpi.total": "الأجهزة",
    "lis.dashboard.kpi.actifs": "روابط نشطة",
    "lis.dashboard.kpi.attention": "تحت المراقبة",
    "lis.dashboard.kpi.quarantaine": "في الحجر",
    "lis.dashboard.detail": "التفاصيل والتشخيص",

    "lis.health.actif": "نشط",
    "lis.health.inactif": "غير نشط",
    "lis.health.attention": "تحت المراقبة",
    "lis.health.absent": "إعداد غائب",
    "lis.health.unknown": "غير معروف",
    "lis.health.connecte": "متصل",
    "lis.health.degrade": "متدهور",
    "lis.health.deconnecte": "غير متصل",
    "lis.link.lastSeen": "آخر ظهور",
    "lis.link.reason": "السبب",

    "lis.config.protocole": "البروتوكول",
    "lis.config.transport": "النقل",
    "lis.config.sens": "الاتجاه",
    "lis.config.mode": "نمط التفاعل",
    "lis.config.ip": "عنوان IP",
    "lis.config.port": "المنفذ",
    "lis.config.liaison": "ربط العينة",
    "lis.config.actif": "الرابط نشط",
    "lis.config.save": "حفظ الإعداد",
    "lis.config.saved": "تم حفظ الإعداد.",
    "lis.config.saveError": "فشل حفظ الإعداد.",
    "lis.transport.serie": "تسلسلي (RS-232)",
    "lis.transport.tcp": "شبكة (TCP/IP)",
    "lis.sens.unidirectionnel": "أحادي الاتجاه (إرسال)",
    "lis.sens.bidirectionnel": "ثنائي الاتجاه (استعلام)",
    "lis.mode.host_query": "استعلام المضيف",
    "lis.mode.push": "إرسال النتائج",
    "lis.mode.download": "تنزيل",

    "lis.test.running": "جارٍ الاختبار…",
    "lis.test.ok": "الرابط مطابق — يمكن للجهاز التواصل.",
    "lis.test.fail": "الرابط غير مطابق.",
    "lis.test.reason.config_absente": "الإعداد غائب: أكمل الربط.",
    "lis.test.reason.invalide": "الإعداد غير متوافق مع المحول.",

    "lis.onboarding.title": "ربط جهاز",
    "lis.onboarding.subtitle": "اختيار موجّه — من الصانع إلى التوصيل",
    "lis.onboarding.step.fabricant": "الصانع",
    "lis.onboarding.step.modele": "الطراز",
    "lis.onboarding.step.protocole": "البروتوكول",
    "lis.onboarding.step.connexion": "التوصيل",
    "lis.onboarding.step.fabricant.hint": "اختر صانع المحلل.",
    "lis.onboarding.step.modele.hint": "{n} طراز في كتالوج الجزائر.",
    "lis.onboarding.step.protocole.hint":
      "يملأ الملف البروتوكول والنقل الموصى بهما مسبقًا — عدّلهما عند الحاجة.",
    "lis.onboarding.step.connexion.hint": "الخطوة الأخيرة: الرمز الداخلي ونقطة التوصيل.",
    "lis.onboarding.disciplineFilter": "التخصص",
    "lis.onboarding.disciplineAll": "كل التخصصات",
    "lis.onboarding.confidence.verified": "مصدر موثّق",
    "lis.onboarding.confidence.inferred": "يُؤكَّد عند التوصيل",
    "lis.onboarding.notes": "ملاحظات اللهجة",
    "lis.onboarding.fabricantNote": "ملاحظة الصانع",
    "lis.onboarding.field.code": "الرمز الداخلي للجهاز",
    "lis.onboarding.field.code.hint": "معرّف قصير وفريد (مثال BIO-1).",
    "lis.onboarding.field.libelle": "التسمية",
    "lis.onboarding.field.ip": "عنوان IP للجهاز",
    "lis.onboarding.field.port": "المنفذ",
    "lis.onboarding.serial.note": "رابط تسلسلي: يُضبط منفذ COM بعد الربط في تفاصيل الجهاز.",
    "lis.onboarding.summary": "الملخص",
    "lis.onboarding.submit": "ربط الجهاز",
    "lis.onboarding.success": "تم ربط الجهاز {code}.",
    "lis.onboarding.error": "فشل الربط.",
    "lis.onboarding.noProfiles": "لا يوجد طراز مطابق للبحث.",
    "lis.onboarding.pickFabricant": "اختر صانعًا أولًا.",
    "lis.onboarding.selected": "محدد",

    "lis.monitor.title": "تدفق الاستيعاب والحجر",
    "lis.monitor.subtitle": "رسائل بانتظار الحل — لا شيء يُفقد",
    "lis.monitor.tab.quarantaine": "الحجر",
    "lis.monitor.tab.mappings": "المطابقات",
    "lis.monitor.motif.all": "كل الأسباب",
    "lis.monitor.motif.code_inconnu": "رمز غير معروف",
    "lis.monitor.motif.orphelin": "عينة يتيمة",
    "lis.monitor.motif.discordance_grave": "تعارض خطير",
    "lis.monitor.col.recu": "استُلم",
    "lis.monitor.col.motif": "السبب",
    "lis.monitor.col.code": "رمز المحلل",
    "lis.monitor.col.specimen": "العينة",
    "lis.monitor.col.valeur": "القيمة الخام",
    "lis.monitor.col.action": "الحل",
    "lis.monitor.empty.title": "قائمة الحجر فارغة",
    "lis.monitor.empty.desc": "كل الرسائل المستلمة طوبقت مع الكتالوج.",
    "lis.monitor.resolve.code": "إنشاء مطابقة",
    "lis.monitor.mappings.pick": "اختر جهازًا",
    "lis.monitor.mappings.empty.title": "لا توجد مطابقات",
    "lis.monitor.mappings.empty.desc": "لا يوجد رمز مطابَق لهذا الجهاز بعد.",
    "lis.monitor.mappings.col.code": "رمز المحلل",
    "lis.monitor.mappings.col.parametre": "معامل المختبر",
    "lis.monitor.mappings.col.unite": "الوحدة",
    "lis.monitor.mappings.col.statut": "الحالة",
    "lis.monitor.mappings.confirm": "تأكيد المطابقة",
    "lis.monitor.mappings.confirmTitle": "تأكيد المطابقة",
    "lis.monitor.mappings.parametreId": "معرّف معامل المختبر",
    "lis.monitor.mappings.parametreId.hint":
      "يربط رمز المحلل بمعامل من الكتالوج؛ تُعاد معالجة النتائج المحجوزة.",
    "lis.monitor.mappings.confirmed": "تم تأكيد المطابقة — أُعيدت معالجة الحجر.",
    "lis.monitor.mappings.confirmError": "فشل تأكيد المطابقة.",
    "lis.monitor.mappings.statut.propose": "مقترح",
    "lis.monitor.mappings.statut.actif": "نشط",

    "lis.chargement.title": "التحميل الموضعي",
    "lis.chargement.subtitle": "اختم ربط الموضع → الأنبوب قبل وصول النتائج",
    "lis.chargement.open": "تحميل جديد",
    "lis.chargement.openTitle": "فتح تحميل",
    "lis.chargement.automate": "الجهاز",
    "lis.chargement.reference": "المرجع (الرف/السلسلة)",
    "lis.chargement.created": "فُتح التحميل {ref}.",
    "lis.chargement.createError": "فشل فتح التحميل.",
    "lis.chargement.lookup": "فتح تحميل موجود",
    "lis.chargement.lookup.id": "معرّف التحميل",
    "lis.chargement.statut.ouvert": "مفتوح",
    "lis.chargement.statut.confirme": "مؤكَّد (مختوم)",
    "lis.chargement.statut.clos": "مغلق",
    "lis.chargement.positions": "المواضع",
    "lis.chargement.addPosition": "إضافة موضع",
    "lis.chargement.col.position": "الموضع",
    "lis.chargement.col.tube": "الأنبوب",
    "lis.chargement.field.position": "الموضع (الرف)",
    "lis.chargement.field.tube": "معرّف الأنبوب",
    "lis.chargement.added": "أُضيف الموضع {pos}.",
    "lis.chargement.addError": "فشل إضافة الموضع.",
    "lis.chargement.empty.title": "لا توجد مواضع",
    "lis.chargement.empty.desc": "أضف كل موضع في الرف قبل ختم التحميل.",
    "lis.chargement.confirm": "ختم التحميل",
    "lis.chargement.confirmTitle": "ختم التحميل",
    "lis.chargement.confirmWarn":
      "الختم نهائي: لا يمكن إضافة أي موضع بعده. يصبح عدد المواضع هو عدد النتائج المتوقعة.",
    "lis.chargement.confirmed": "خُتم التحميل — نطاق الموضع نشط.",
    "lis.chargement.confirmError": "فشل ختم التحميل.",
    "lis.chargement.close": "إغلاق التحميل",
    "lis.chargement.closed": "أُغلق التحميل.",
    "lis.chargement.closeError": "فشل إغلاق التحميل.",
    "lis.chargement.sealedNote": "تحميل مختوم — التعديل غير ممكن.",
    "lis.chargement.expected": "{n} نتيجة متوقعة",

    // وكلاء التتابع السحابي (NOVA-070)
    "lis.agents.title": "وكلاء التتابع",
    "lis.agents.subtitle": "الوكلاء في الموقع يتحاورون مع الأجهزة ويُرحّلون نتائجها إلى السحابة.",
    "lis.agents.add": "تسجيل وكيل",
    "lis.agents.addTitle": "وكيل تتابع جديد",
    "lis.agents.field.nom": "اسم الوكيل (محطة / طاولة)",
    "lis.agents.created": "تم تسجيل الوكيل — أُنشئ رمز الاقتران.",
    "lis.agents.createError": "تعذّر تسجيل الوكيل.",
    "lis.agents.col.nom": "الوكيل",
    "lis.agents.col.statut": "الحالة",
    "lis.agents.col.presence": "آخر حضور",
    "lis.agents.col.version": "الإصدار",
    "lis.agents.col.ip": "IP المُلاحَظ",
    "lis.agents.statut.en_attente": "بانتظار الاقتران",
    "lis.agents.statut.actif": "نشط",
    "lis.agents.statut.revoque": "مُلغى",
    "lis.agents.online": "متصل",
    "lis.agents.offline": "غير متصل",
    "lis.agents.empty.title": "لا يوجد وكيل تتابع",
    "lis.agents.empty.desc": "سجّل وكيلًا لترحيل نتائج جهاز بعيد إلى هذه السحابة.",
    "lis.agents.revoke": "إلغاء",
    "lis.agents.revokeTitle": "إلغاء الوكيل؟",
    "lis.agents.revokeWarn":
      "يُبطَل رمز الوكيل فورًا: لن يتمكن من ترحيل أي نتيجة. الإجراء قابل للعكس باقتران جديد.",
    "lis.agents.revoked": "تم إلغاء الوكيل.",
    "lis.agents.revokeError": "تعذّر الإلغاء.",
    "lis.agents.pairingTitle": "رمز الاقتران — يُعرض مرة واحدة فقط",
    "lis.agents.pairingNote": "انسخ هذا الرمز إلى مُثبّت الوكيل. ينتهي قريبًا ولن يُعرض مجددًا أبدًا.",
    "lis.agents.pairingCopy": "نسخ الرمز",
    "lis.agents.pairingCopied": "تم نسخ الرمز.",
    "lis.agents.pairingExpires": "ينتهي في {at}",
    "lis.agents.done": "تم",
    "lis.agents.installTitle": "تثبيت وكيل التتابع",
    "lis.agents.installStep1":
      "ثبّت حزمة وكيل Novanalyse على المحطة في الموقع المتصلة بالأجهزة (راجع agent/README.md).",
    "lis.agents.installStep2": "عند أول تشغيل يطلب الوكيل رمز الاقتران: الصق الرمز أعلاه.",
    "lis.agents.installStep3":
      "يبادل الوكيل الرمز برمز مميز (مرة واحدة)، ثم يتحاور محليًا مع الأجهزة ويرحّل النتائج إلى هذه السحابة. تتحول الشارة إلى «متصل» عند أول نبضة.",

    "lis.trouble.title": "التشخيص",
    "lis.trouble.subtitle": "فحوص سريعة عند صمت الرابط",
    "lis.trouble.step1.title": "اختبار الرابط",
    "lis.trouble.step1.desc": "شغّل اختبار الاتصال: يتحقق من الإعداد مقابل قدرات المحول.",
    "lis.trouble.step2.title": "تحقق من التوصيل المادي",
    "lis.trouble.step2.desc":
      "رابط الشبكة: IP والمنفذ قابلان للوصول. رابط تسلسلي: الكابل ومنفذ COM والسرعة والتماثل مطابقة للملف.",
    "lis.trouble.step3.title": "تحقق من الاتجاه والنمط",
    "lis.trouble.step3.desc": "جهاز في وضع الاستعلام ينتظر استعلامًا: تأكد أن الاتجاه ثنائي.",
    "lis.trouble.step4.title": "راقب الحجر",
    "lis.trouble.step4.desc":
      "الرسائل المستلمة غير المطابَقة تتراكم في الحجر، ولا تُفقد أبدًا — طابِق الرموز غير المعروفة.",
    "lis.trouble.goMonitor": "فتح الحجر",

    "lis.na": "—",
  });
}
